import {
    LOAD_CATALOG_ITEMS_SUCCESS,
    LOAD_HOME_DATA_SUCCESS,
    LOAD_ITEMS_SUCCESS,
    LOAD_SIMILAR_ITEMS_V3_SUCCESS,
    LOAD_STOREFRONT_ITEMS_SUCCESS,
} from './actions';
import api from '@/redux/api/item';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_ITEM_BUY_NOW_FAIL = 'la/domain/buyNow/LOAD_FAIL';
export const LOAD_ITEM_BUY_NOW_REQUEST = 'la/domain/buyNow/LOAD_REQUEST';
export const LOAD_ITEM_BUY_NOW_SUCCESS = 'la/domain/buyNow/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_ITEM_BUY_NOW_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.itemIds),
            };
        case LOAD_ITEM_BUY_NOW_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_SIMILAR_ITEMS_V3_SUCCESS:
            existing = cloneDeep(state.byId);
            action.payload.forEach(({ buyNow, item }) => {
                if (!existing[item.itemId]) {
                    existing[item.itemId] = buyNow;
                }
            });
            return {
                ...state,
                byId: existing,
            };
        case LOAD_STOREFRONT_ITEMS_SUCCESS:
        case LOAD_CATALOG_ITEMS_SUCCESS:
        case LOAD_HOME_DATA_SUCCESS:
        case LOAD_ITEM_BUY_NOW_SUCCESS:
        case LOAD_ITEMS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.buyNows) {
                action.payload.buyNows.forEach((x) => {
                    existing[x.itemId] = { ...x };
                    loaded[x.itemId] = time;
                    loading = difference(loading, [x.itemId]);
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.buyNow;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

// takes in a (state, id) and passes both params to every function on
// the way down.
export const getBuyNow: (state: GlobalState, itemId: number) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) =>
        byId[id] || {
            price: 0,
            status: 'unavailable',
        }
);

const getLoadTimeForBuyNow = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isBuyNowLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchBuyNow = (state, itemId) => {
    if (!itemId) {
        return false;
    }
    const item = getBuyNow(state, itemId);
    if (item) {
        const loaded = getLoadTimeForBuyNow(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isBuyNowLoading(state, itemId);
    return !loading;
};

const whichBuyNowsNeeded = (state, itemIds) => itemIds.filter((itemId) => shouldFetchBuyNow(state, itemId));

/* ACTION CREATORS */
const loadBuyNows = (itemIds) => async (dispatch, getState) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        dispatch({
            payload: itemIds,
            type: LOAD_ITEM_BUY_NOW_REQUEST,
        });

        const response = await api.fetchBuyNowsByIds({ deployment, itemIds });

        dispatch({
            meta: { actionTime: Date.now(), itemIds },
            payload: response.data,
            type: LOAD_ITEM_BUY_NOW_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemIds },
            payload: error,
            type: LOAD_ITEM_BUY_NOW_FAIL,
        });
    }
};

export const fetchBuyNowsIfNeeded = (itemIds: number[]) => async (dispatch: Function, getState: Function) => {
    const needed = whichBuyNowsNeeded(getState(), itemIds);
    if (needed.length) {
        return dispatch(loadBuyNows(needed));
    }
    return Promise.resolve();
};
